import { useState } from 'react';
import { ExternalLink, Pencil } from 'lucide-react';
import type * as Y from 'yjs';

import { AiSurface } from '@/components/ui/ai-surface';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { CitationSourceModal } from '@/components/modals/citation-source-modal';
import { ProvenanceChip, type Origin as ProvenanceOrigin } from '@/components/ui/provenance-chip';
import { SeverityChip } from '@/components/ui/severity-chip';
import { VerbChip, type SubmittalVerb } from '@/components/ui/verb-chip';

import { YjsCell } from './yjs-cell';
import { RegisterCell } from './register-cell';
import { cn } from '@/lib/utils/cn';
import type { RegisterEntry } from '@/lib/api/registers';

/**
 * RegisterRow — a single row in the register grid.
 *
 * Visual contract (prototype `Screens.register` → `riskRow`): the prototype
 * renders columns
 *   ID · Title (+ AI proposal chip + confidence + citation) · Domain · Severity
 *   · Likelihood · Owner · Status · Evidence · Origin · Action.
 *
 * Severity is the stored word itself in `<SeverityChip>` (C2-55), never a
 * posture: posture is the engagement rollup, severity belongs to the row.
 * Domain reads `payload.domain` (risks) and falls back to the register
 * entry's `health_domain_name` (findings keep their domain in a column,
 * C2-56); Owner reads `payload.owner` and falls back to
 * `payload.recommended_owner` (findings).
 *
 * The TA contract for T-0098 layers in two more axes — `vendor_claim` and
 * `avero_verification` — that the simpler prototype omits but the IV&V
 * verification rule (§8 pattern 2) treats as mandatory display state. We
 * surface those as a second compact row beneath the title so the prototype
 * column rhythm reads cleanly while preserving the dual-axis cells (and the
 * Yjs CRDT cell semantics that bind to them).
 *
 * AI provenance rows wrap the entire `<tr>` in `<AiSurface as="tr">` per
 * §6 / §8: AI content is *never* a regular row visually.
 *
 * Yjs integration: when the relay is connected (`doc !== undefined`), the
 * two editable cells (vendor_claim + avero_verification) render `<YjsCell>`,
 * keeping the T-0032 live-edit contract. When the relay is unavailable, the
 * row falls back to `<RegisterCell>` which speaks the REST PATCH path.
 *
 * Client-viewer scope: when `readOnly` is true (passed from the route when
 * `viewerGrantId` is present), every edit affordance is hidden and the row
 * renders the frozen snapshot state.
 *
 * Action button: AI-proposed rows render "Review" (highlighted), authored or
 * confirmed rows render an edit pencil. Both carry `data-open-override="<id>"`
 * so the global modal host (T-0113 modal context) picks them up.
 */
export interface RegisterRowProps {
  entry: RegisterEntry;
  /** Translated UI-facing provenance enum — see backendOriginToProvenance. */
  provenance: ProvenanceOrigin;
  /** Yjs doc if relay is connected; falsy → REST PATCH path. */
  doc?: Y.Doc;
  /** Hide every edit affordance (viewer / snapshot scope). */
  readOnly: boolean;
  /** Whether the row is currently selected. */
  selected: boolean;
  onSelectChange: (next: boolean) => void;
  /** Manual-mode commit handler (REST PATCH path). */
  onManualSave: (input: { vendor_claim?: string; avero_verification?: string }) => void;
  /** Whether a mutation is currently in flight for this row. */
  saving: boolean;
  /** Engagement id for the citation modal. */
  engagementId: string;
}

export function RegisterRow({
  entry,
  provenance,
  doc,
  readOnly,
  selected,
  onSelectChange,
  onManualSave,
  saving,
  engagementId,
}: RegisterRowProps) {
  const [openCite, setOpenCite] = useState<CitationChipData | null>(null);

  const display = derivePayloadDisplay(entry.payload);
  const domain = display.domain ?? entry.health_domain_name ?? undefined;
  const statusChip = deriveStatusChip(entry.status, display.severity);
  const shortId = `R-${entry.id.slice(0, 4).toUpperCase()}`;
  const isAi =
    provenance === 'ai_proposed' ||
    provenance === 'ai_confirmed' ||
    provenance === 'consultant_edited';

  // The dual-axis cells (T-0098) live in a second row below the title.
  // Hidden when both axes are at their default "untouched" values to keep
  // the grid scannable — clicking the title row still reveals them on
  // hover via the `tr.peer-hover` rule.
  const showDualAxis =
    (entry.vendor_claim && entry.vendor_claim !== 'pending') ||
    (entry.avero_verification && entry.avero_verification !== 'unverified') ||
    !readOnly;

  const cells = (
    <>
      <td className="w-10 px-3">
        {readOnly ? null : (
          <input
            type="checkbox"
            checked={selected}
            onChange={(e) => onSelectChange(e.target.checked)}
            aria-label={`Select row ${shortId}`}
            className="h-3.5 w-3.5 rounded-sm border-ink-300 text-accent-amber-600 focus:ring-accent-amber-600"
          />
        )}
      </td>

      <td className="w-20 px-2 align-top">
        <span className="font-mono text-[11px] text-ink-700">{shortId}</span>
      </td>

      <td className="px-2 align-top">
        <div className="space-y-1">
          <div className="flex items-start gap-2">
            <span className="text-body-sm font-medium text-ink-900">
              {display.title || (
                <span className="italic text-ink-500">Untitled — set a title on this entry</span>
              )}
            </span>
            {display.verb ? <VerbChip verb={display.verb} /> : null}
          </div>

          {(isAi && entry.original_ai_confidence != null) || display.cites.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              {provenance === 'ai_proposed' ? (
                <span className="inline-flex h-5 items-center rounded-chip bg-ai-surface px-1.5 text-[11px] font-medium text-ai-ink">
                  AI proposal
                </span>
              ) : null}
              {isAi && entry.original_ai_confidence != null ? (
                <AiConfidenceChip value={entry.original_ai_confidence} />
              ) : null}
              {display.cites.map((c) => (
                <CitationChip key={c.id} cite={c} onOpen={() => setOpenCite(c)} />
              ))}
            </div>
          ) : null}

          {/* Dual-axis verification cells (T-0098 §8 pattern 2). Surface
              under the title in a compact row so they never collapse to a
              single status. */}
          {showDualAxis ? (
            <div className="flex flex-wrap items-stretch gap-2 pt-1">
              <div className="flex min-w-[180px] flex-1 items-center gap-1.5 rounded-card border border-ink-100 bg-paper-50 px-2 py-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">
                  Vendor
                </span>
                {doc ? (
                  <YjsCell
                    doc={doc}
                    entryId={entry.id}
                    cellKey="vendor_claim"
                    initialValue={entry.vendor_claim}
                  />
                ) : (
                  <RegisterCell
                    value={entry.vendor_claim}
                    onCommit={(next) => onManualSave({ vendor_claim: next })}
                    readOnly={readOnly}
                    entryId={entry.id}
                    cellKey="vendor_claim"
                    saving={saving}
                    placeholder="Vendor claim…"
                  />
                )}
              </div>
              <div className="flex min-w-[180px] flex-1 items-center gap-1.5 rounded-card border border-ink-100 bg-paper-50 px-2 py-1">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-500">
                  Avero
                </span>
                {doc ? (
                  <YjsCell
                    doc={doc}
                    entryId={entry.id}
                    cellKey="avero_verification"
                    initialValue={entry.avero_verification}
                  />
                ) : (
                  <RegisterCell
                    value={entry.avero_verification}
                    onCommit={(next) => onManualSave({ avero_verification: next })}
                    readOnly={readOnly}
                    entryId={entry.id}
                    cellKey="avero_verification"
                    saving={saving}
                    placeholder="Avero verification…"
                  />
                )}
              </div>
            </div>
          ) : null}
        </div>
      </td>

      <td className="w-32 px-2 align-top">
        <span className="text-body-sm text-ink-700">{domain ?? '—'}</span>
      </td>

      <td className="w-24 px-2 align-top">
        <SeverityChip severity={display.severity} />
      </td>

      <td className="w-24 px-2 align-top">
        <span className="text-body-sm capitalize text-ink-700">{display.likelihood ?? '—'}</span>
      </td>

      <td className="w-32 px-2 align-top">
        <span className="text-body-sm text-ink-700">{display.owner ?? '—'}</span>
      </td>

      <td className="w-28 px-2 align-top">{statusChip}</td>

      <td className="w-24 px-2 align-top">
        <button
          type="button"
          aria-label="View evidence"
          data-open-evidence={`register_entry:${entry.id}`}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-ink-700 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          <ExternalLink className="h-3 w-3" aria-hidden="true" />
          {display.cites.length}
        </button>
      </td>

      <td className="w-44 px-2 align-top">
        <ProvenanceChip
          origin={provenance}
          lineage={
            entry.origin_set_at
              ? {
                  // The whole timestamp: the chip prints it as a local date.
                  confirmed_at: entry.origin_set_at,
                  // The backend doesn't surface a confirmer name yet — leave
                  // it blank; the tooltip degrades gracefully when fields are
                  // missing.
                }
              : undefined
          }
        />
        {entry.override_tag ? (
          <div className="mt-1 text-[10px] text-ink-500">tag: {entry.override_tag}</div>
        ) : null}
      </td>

      <td className="w-24 px-2 align-top">
        {readOnly ? null : provenance === 'ai_proposed' ? (
          <button
            type="button"
            data-open-override={entry.id}
            className="inline-flex h-7 items-center rounded-chip border border-accent-amber-600 bg-accent-amber-100 px-2.5 text-[11px] font-semibold text-accent-amber-700 hover:bg-accent-amber-50"
          >
            Review
          </button>
        ) : (
          <button
            type="button"
            data-open-override={entry.id}
            aria-label="Override"
            className="inline-flex h-7 w-7 items-center justify-center rounded-chip border border-ink-100 bg-paper-0 text-ink-700 hover:border-ink-300"
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </td>
    </>
  );

  const sharedClassName = cn('align-top text-body-sm hover:bg-ink-100/30');

  if (isAi) {
    return (
      <>
        <AiSurface
          as="tr"
          data-row-id={entry.id}
          data-register-type={entry.register_type}
          data-provenance={provenance}
          className={sharedClassName}
        >
          {cells}
        </AiSurface>
        {openCite ? (
          <CitationSourceModal
            engagementId={engagementId}
            cite={openCite}
            open={!!openCite}
            onOpenChange={(o) => {
              if (!o) setOpenCite(null);
            }}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <tr
        data-row-id={entry.id}
        data-register-type={entry.register_type}
        data-provenance={provenance}
        className={sharedClassName}
      >
        {cells}
      </tr>
      {openCite ? (
        <CitationSourceModal
          engagementId={engagementId}
          cite={openCite}
          open={!!openCite}
          onOpenChange={(o) => {
            if (!o) setOpenCite(null);
          }}
        />
      ) : null}
    </>
  );
}

/** Render a status chip — prototype maps `Open` → unassessed tone,
 *  `Mitigating` → at-risk tone, `Closed`/`Resolved` → healthy tone. */
function deriveStatusChip(status: string, severity?: string): JSX.Element {
  const label = status ? status.charAt(0).toUpperCase() + status.slice(1) : '—';
  if (status === 'closed' || status === 'resolved' || status === 'completed') {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-posture-healthy-tint px-2.5 text-[12px] font-medium text-posture-healthy">
        {label}
      </span>
    );
  }
  if (status === 'mitigating' || severity === 'medium') {
    return (
      <span className="inline-flex h-[22px] items-center rounded-full bg-posture-at-risk-tint px-2.5 text-[12px] font-medium text-posture-at-risk">
        {label}
      </span>
    );
  }
  return (
    <span className="inline-flex h-[22px] items-center rounded-full bg-ink-100 px-2.5 text-[12px] font-medium text-posture-unassessed">
      {label}
    </span>
  );
}

/**
 * Pluck title + cites + severity + likelihood + owner + domain + verb from
 * the entry's `payload`. The payload shape is intentionally `unknown`
 * upstream because different register types stash different things — we
 * look for the conventional keys and degrade gracefully on each.
 */
interface PayloadDisplay {
  title: string;
  cites: CitationChipData[];
  severity?: string;
  likelihood?: string;
  owner?: string;
  domain?: string;
  verb?: SubmittalVerb;
}

function derivePayloadDisplay(payload: unknown): PayloadDisplay {
  const empty: PayloadDisplay = { title: '', cites: [] };
  if (!payload || typeof payload !== 'object') return empty;
  const obj = payload as Record<string, unknown>;

  const title = stringOr(obj.title, stringOr(obj.name, ''));
  const severity = nonBlank(obj.severity);
  const likelihood = nonBlank(obj.likelihood);
  // Risks and issues name an `owner`; findings a `recommended_owner`.
  const owner = nonBlank(obj.owner) ?? nonBlank(obj.recommended_owner);
  const domain = nonBlank(obj.domain);
  const verb = isSubmittalVerb(obj.verb) ? (obj.verb as SubmittalVerb) : undefined;

  const cites: CitationChipData[] = [];
  const rawCites = Array.isArray(obj.citations) ? obj.citations : [];
  for (const c of rawCites) {
    if (!c || typeof c !== 'object') continue;
    const co = c as Record<string, unknown>;
    const docLabel = typeof co.doc_label === 'string' ? (co.doc_label as string) : undefined;
    if (!docLabel) continue;
    const confidence = typeof co.confidence === 'number' ? (co.confidence as number) : 0;
    cites.push({
      id:
        typeof co.id === 'string'
          ? (co.id as string)
          : `${docLabel}|${stringOr(co.section, '')}|${stringOr(co.page, '')}|${stringOr(co.cell, '')}`,
      doc_label: docLabel,
      section: typeof co.section === 'string' ? (co.section as string) : null,
      page: typeof co.page === 'number' ? (co.page as number) : null,
      cell: typeof co.cell === 'string' ? (co.cell as string) : null,
      confidence,
      status: co.status === 'broken' ? 'broken' : 'ok',
    });
  }

  return { title, cites, severity, likelihood, owner, domain, verb };
}

function stringOr(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

/** The string when it has content once trimmed; undefined otherwise. */
function nonBlank(v: unknown): string | undefined {
  return typeof v === 'string' && v.trim().length > 0 ? v : undefined;
}

function isSubmittalVerb(v: unknown): v is SubmittalVerb {
  return (
    typeof v === 'string' &&
    (
      [
        'approved',
        'approved_as_noted',
        'revise',
        'rejected',
        'outstanding',
        'overdue',
        'submitted',
      ] as readonly string[]
    ).includes(v)
  );
}
